import { formatInTimeZone } from "date-fns-tz";
import React from "react";
import type { Highlight } from "src/components/DocumentViewer/V2/types";
import { parseAnalyzeIdResponse } from "src/components/Form/utils/analyzeIdUtils";
import { DEFAULT_DATE_TYPE_FORMAT } from "src/constants/date";
import { PersonAddressSchema } from "src/schemas/Address";
import { formatIsoDate } from "src/services/format";
import { isNotNull } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import {
  IdentityAnalyzeIdMatchType,
  matchAnswersToAnalyzeId,
  matchProfileToAnalyzeId,
  Profile,
  ProfileFieldType,
  type IdentityAnalyzeIdMatch,
} from "./analyzeIdFormMatchUtils";

/** e.g. FIRST_NAME → "First name", DATE_OF_BIRTH → "Date of birth" */
function analyzeIdFieldSentenceCase(fieldType: string): string {
  const spaced = fieldType.replace(/_/g, " ").trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatAnswerForHighlight(
  question: AF.Question<AF.WithId>,
  answer: unknown,
  timezoneName: string
): string {
  if (!isNotNull(answer)) {
    return "";
  }

  switch (question.type) {
    case AF.DateType: {
      return formatInTimeZone(
        answer.toString(),
        timezoneName,
        question.constraints?.format || DEFAULT_DATE_TYPE_FORMAT
      );
    }
    case AF.AddressType:
      if (typeof answer === "object") {
        const addressAnswer = PersonAddressSchema.parse(answer);
        return `${addressAnswer.street_address}${
          addressAnswer.street_address_line_2
            ? " " + addressAnswer.street_address_line_2
            : ""
        }, ${addressAnswer.city}, ${addressAnswer.state} ${
          addressAnswer.zip_code
        }`;
      }
      return "";
    default:
      return answer.toString();
  }
}

/**
 * Per Textract field type, lines: `Matches <Question label>: <answer>`.
 */
function buildAnalyzeIdFieldTypeToDescriptionLines(
  matches: IdentityAnalyzeIdMatch[],
  questions: AF.Question<AF.WithId>[],
  answers: Record<string, unknown>,
  profiles: Profile[],
  timezoneName: string
): Map<string, string[]> {
  const byField = new Map<string, string[]>();

  for (const row of matches) {
    if (!row.highlightAnalyzeIdFieldTypes.length) continue;

    let line: string = "";
    if (row.matchType === IdentityAnalyzeIdMatchType.Question) {
      const q = questions.find((x) => x.id === row.id);
      if (!q) continue;
      const ans = formatAnswerForHighlight(q, answers[row.id], timezoneName);
      line = `Matches ${q.question}: ${ans}`;
    } else {
      // Profile type
      const p = profiles.find((x) => x.id === row.id);
      if (!p) continue;
      const name = [p.first_name, p.middle_name, p.last_name]
        .filter((part) => !!part)
        .join(" ");
      line = `Matches ${p.person_type} ${name}`;
      switch (row.profileFieldType) {
        case ProfileFieldType.DateOfBirth:
          if (p.birth_date)
            line += `'s date of birth: ${formatIsoDate(
              p.birth_date ?? "",
              "MM/dd/yyyy"
            )}`;
          break;
        case ProfileFieldType.Address:
          line += `'s address: ${p.street_address}${
            p.street_address_line_2 ? " " + p.street_address_line_2 : ""
          }, ${p.city}, ${p.state} ${p.zip_code}`;
          break;
      }
    }
    if (!line) continue;

    for (const fieldType of row.highlightAnalyzeIdFieldTypes) {
      let lines = byField.get(fieldType);
      if (!lines) {
        lines = [];
        byField.set(fieldType, lines);
      }
      lines.push(line);
    }
  }

  return byField;
}

/**
 * Highlights for matched AnalyzeID fields
 * Title: `<ID field name>: <extracted value>`
 * Description: `Matches <label>: <answer>` lines
 */
export function computeIdentityMatches(
  rawResults: Record<string, unknown> | null | undefined,
  questions: AF.Question<AF.WithId>[] | undefined,
  answers: Record<string, any> | undefined,
  profiles: Profile[],
  imageDimensions: { width: number; height: number; loaded: boolean },
  timezoneName: string
): {
  answerMatches: IdentityAnalyzeIdMatch[];
  profileMatches: IdentityAnalyzeIdMatch[];
  highlights: Highlight[];
} {
  if (!rawResults || !questions || !answers || !imageDimensions.loaded) {
    return {
      answerMatches: [],
      profileMatches: [],
      highlights: [],
    };
  }
  const parsed = parseAnalyzeIdResponse(
    rawResults,
    imageDimensions.width,
    imageDimensions.height
  );
  const answerMatches = matchAnswersToAnalyzeId(questions, answers, parsed);
  const profileMatches = matchProfileToAnalyzeId(profiles, parsed);
  const matches = [...answerMatches, ...profileMatches];

  const fieldToDescriptionLines = buildAnalyzeIdFieldTypeToDescriptionLines(
    matches,
    questions,
    answers,
    profiles,
    timezoneName
  );

  const typesWithMatches = matches
    .map((m) => m.highlightAnalyzeIdFieldTypes)
    .flat();
  const highlights: Highlight[] = [];
  for (const p of parsed) {
    if (!p.polygon || !typesWithMatches.includes(p.type)) continue;
    const title = `${analyzeIdFieldSentenceCase(p.type)}: ${p.value}`;
    const descriptionLines = fieldToDescriptionLines.get(p.type);
    const description = descriptionLines?.join("\n") ?? "";
    highlights.push({
      id: p.id,
      polygon: p.polygon,
      title,
      description,
    });
  }

  return { answerMatches, profileMatches, highlights };
}

export function useImageDimensions(url?: string): {
  width: number;
  height: number;
  loaded: boolean;
  unsupportedFileType: boolean;
} {
  const [dims, setDims] = React.useState({
    width: 0,
    height: 0,
    loaded: false,
    unsupportedFileType: false,
  });

  React.useEffect(() => {
    if (!url) {
      setDims({
        width: 0,
        height: 0,
        loaded: false,
        unsupportedFileType: false,
      });
      return;
    }

    // Until the new image has fired onload, we must not use the previous
    // url's natural dimensions (highlights + polygon layout would be wrong).
    setDims({ width: 0, height: 0, loaded: false, unsupportedFileType: false });

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setDims({
        width: img.naturalWidth,
        height: img.naturalHeight,
        loaded: true,
        unsupportedFileType: false,
      });
    };
    img.onerror = () => {
      setDims({
        width: 0,
        height: 0,
        loaded: false,
        unsupportedFileType: true,
      });
    };
    img.src = url;

    return () => {
      img.src = "";
    };
  }, [url]);

  return dims;
}
